import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import * as moment from 'moment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import BusinessTypeModel from '../../../core/models/business-type.model';
import { BusinessTypeService } from '../../../core/services/business-type.service';
import BusinessCategoryModel from '../../../core/models/business-category.model';
import { BusinessCategoryService } from '../../../core/services/business-category.service';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";

/**
 * @component BusinessTypeComponent
 * @description
 * This component manages the "Business Type" master data within the Admin Panel > User Access section.
 * It provides a comprehensive interface for Administrators to Create, Read, Update, and Delete (CRUD)
 * business type definitions.
 *
 * Business Types are often categorized under "Business Categories", so this component also fetches
 * and manages the relationship with Business Categories via a dropdown selection.
 *
 * **Key Responsibilities:**
 * 1. **Data Visualization**: Displays a paginated/infinite-scrolled list of existing business types.
 * 2. **Data Management**: Provides a Reactive Form for adding new types or editing existing ones.
 * 3. **Relationship Management**: Links Business Types to specific Business Categories.
 * 4. **Validation**: Enforces strict validation rules on names, codes, and descriptions.
 * 5. **Duplicate Prevention**: Implements client-side logic to detect and warn about duplicate type names.
 *
 * **Data Flow:**
 * - On initialization (`ngOnInit`), the component fetches:
 *   - All Business Types via `BusinessTypeService`.
 *   - All Business Categories via `BusinessCategoryService` (for the dropdown).
 * - The raw data is stored in `businessTypes`.
 * - A `BatchLoader` utility is initialized to handle client-side pagination (infinite scroll).
 * - The UI binds to `businessTypesList`, which contains the currently loaded subset of data.
 *
 * **User Roles & Permissions:**
 * - Restricted to **Super Admin** or **System Admin** roles.
 *
 * **UI Features:**
 * - **Infinite Scroll**: Automatically loads more items as the user scrolls down.
 * - **Debounced Search**: Search input waits for user to stop typing before filtering.
 * - **Dynamic Form**: Form layout adjusts based on field count.
 *
 * @selector app-business-type
 * @implements OnInit, OnDestroy
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Component({
  selector: 'app-business-type',
  templateUrl: './business-type.component.html',
  styleUrls: ['./business-type.component.scss']
})
export class BusinessTypeComponent implements OnInit, OnDestroy {
  // ==========================================================================
  // DATA PROPERTIES
  // ==========================================================================

  /**
   * @type {BusinessTypeModel[]}
   * @description Master list of all business types fetched from the backend.
   * @default []
   */
  public businessTypes: BusinessTypeModel[] = [];

  /**
   * @type {BusinessTypeModel[]}
   * @description The subset of business types currently displayed in the UI.
   * This list grows as the user scrolls down (infinite scroll implementation).
   * @default []
   */
  public businessTypesList: BusinessTypeModel[] = [];

  /**
   * @type {BusinessTypeModel}
   * @description The model used for binding to the Add/Edit form.
   * Represents the type currently being created or modified.
   */
  public businessType: BusinessTypeModel = new BusinessTypeModel();

  /**
   * @type {BusinessTypeModel[]}
   * @description Stores the results of a search query.
   * Used by the BatchLoader when `isFiltering` is true.
   * @default []
   */
  public filteredBusinessTypes: BusinessTypeModel[] = [];

  /**
   * @type {BusinessCategoryModel[]}
   * @description List of available business categories for the dropdown selection.
   * @default []
   */
  public businessCategories: BusinessCategoryModel[] = [];

  /**
   * @type {BusinessCategoryModel}
   * @description Holds the selected category object (if needed for specific logic).
   */
  public businessCategory: BusinessCategoryModel;

  /** @type {boolean} @description Page load state tracker. */
  public pgLoad: boolean;

  /**
   * @type {string}
   * @description The current search string entered by the user.
   */
  public search: string;

  // ==========================================================================
  // FORM & STATE PROPERTIES
  // ==========================================================================

  /**
   * @type {boolean}
   * @description Flag indicating if the form has been submitted.
   * @default false
   */
  public submitted = false;

  /**
   * @type {FormGroup}
   * @description Reactive form group for adding/updating business types.
   * Contains controls for `name`, `businesscategory`, `code`, and `description`.
   */
  public addBusinessCategoryForm: FormGroup;

  /** @type {boolean} @description Flag for "Create" mode. */
  public submitButton: boolean;
  /** @type {boolean} @description Flag for "Update" mode. */
  public updateButton: boolean;

  /**
   * @type {any}
   * @description Stores the original name of the type being edited for validation purposes.
   */
  public originalBusinessTypes: any;

  /** @type {number} @description Index tracker for list operations. */
  public index: number;

  /**
   * @type {boolean}
   * @description Prevents double-submission of the form by locking the submit action.
   */
  public businessTypeDoubleClick: boolean;

  // ==========================================================================
  // UI STATE PROPERTIES
  // ==========================================================================

  /** @type {boolean} @description Flag to display "No Data" message. @default false */
  public NoDataMessage: boolean = false;
  /** @type {number} @description Tracks active page number. */
  public activePageNo: number;
  /** @type {boolean} @description Trigger to clear search input. @default false */
  public clearSearchInput: boolean = false;
  /** @type {boolean} @description Controls main page loader visibility. @default false */
  public enableDisablePageLoader: boolean = false;
  /** @type {boolean} @description Controls list-specific loader visibility. @default false */
  public listLoader = false;              // Show listLoader spinner
  /** @type {number} @description Tracks loaded data index. @default 0 */
  public startIndex = 0;             // Tracks how much has been loaded
  /** @type {boolean} @description Indicates if filtering is active. @default false */
  public isFiltering: boolean = false;
  /** @type {boolean} @description Indicates if filtered list is empty. @default false */
  public noList: boolean = false;
  /** @type {any} @description Timeout reference for search debounce. */
  searchTimeout: any;
  /** @type {number} @description Count of form fields for layout adjustment. @default 0 */
  public formFieldCount: number = 0;
  /** @type {boolean} @description Toggles form visibility. @default false */
  public showForm: boolean = false;

  /**
   * @method constructor
   * @description Initializes the component, injects dependencies, and builds the reactive form.
   *
   * @param {ToastrService} toasterService - @service For displaying notifications.
   * @param {Sidenavaction} sidenavaction - @service For sidebar navigation control.
   * @param {FormBuilder} formBuilder - @service For creating the reactive form.
   * @param {BusinessTypeService} businessTypeService - @service For Business Type API operations.
   * @param {BusinessCategoryService} businessCategoryService - @service For Business Category API operations.
   * @param {CommonMethodsService} commonMethodsService - @service For utility methods.
   * @param {LocationStrategy} locationStrategy - @service For URL path interaction.
   * @param {PreviousRouteService} previousRouteService - @service For navigation history.
   */
  constructor(public toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public businessTypeService: BusinessTypeService, public businessCategoryService: BusinessCategoryService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.businessType = new BusinessTypeModel();
    /*form validation*/
    this.addBusinessCategoryForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      businesscategory: ['', [Validators.required]],
      code: ['', [Validators.minLength(3), Validators.maxLength(10), Validators.pattern('^[A-Za-z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.addBusinessCategoryForm.controls).length;
  }

  /**
   * @private
   * @type {BatchLoader<any>}
   * @description Utility instance for handling client-side pagination/infinite scroll.
   */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @lifecycle
   * @async
   * @description Component initialization lifecycle hook.
   * 1. Sets up navigation and resets state.
   * 2. Calls `afterComponentInitialize` to fetch Business Types.
   * 3. Fetches Business Categories for the dropdown.
   * 4. Handles error reporting via `CommonMethodsService`.
   *
   * @throws {Error} If API calls fail.
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.businessTypes = [];
    this.businessTypesList = [];
    this.filteredBusinessTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      await this.afterComponentInitialize();
      this.businessCategories = await this.businessCategoryService.getAllBusinessCategories();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method afterComponentInitialize
   * @async
   * @description Helper method to fetch and process Business Types.
   * - Fetches all business types.
   * - Formats `updatedAt` dates.
   * - Initializes the `BatchLoader` for the list view.
   *
   * @returns {Promise<void>}
   */
  public async afterComponentInitialize() {
    try {
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      if (this.businessTypes.length > 0) {
        this.businessTypes.forEach(eachBusinessType => {
          eachBusinessType.updatedAt = JSON.parse(JSON.stringify(moment(eachBusinessType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events.
   * Triggers `checkScrollAndLoad` to handle infinite scrolling.
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /** @private @type {any} @description Timeout reference for scroll throttling. */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if the user has scrolled near the bottom of the page.
   * If threshold reached, triggers `batchLoader` to load the next set of records.
   *
   * @side-effect Modifies `businessTypesList`.
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Configures the `BatchLoader` utility.
   * Defines data source (filtered vs full) and UI update callback.
   *
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredBusinessTypes : this.businessTypes,
      (updatedList) => {
        this.businessTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  //To cancel ongoing requests and unsubscribe from subscriptions.
  /**
   * @method ngOnDestroy
   * @lifecycle
   * @description Cleanup lifecycle hook.
   * Cancels pending HTTP requests and destroys the batch loader.
   */
  public ngOnDestroy(): void {
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  // soil type register form convenience getter for easy access to form fields
  /**
   * @method businessCategoryFormValidation
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {AbstractControl} The form controls of `addBusinessCategoryForm`.
   */
  public get businessCategoryFormValidation() {
    return this.addBusinessCategoryForm.controls;
  }

  // search functionality
  /**
   * @method applySearch
   * @async
   * @description Filters the business type list based on `search` string.
   * - Matches against `name` or `code`.
   * - Resets `BatchLoader` to display filtered results.
   *
   * @returns {Promise<void>}
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.businessTypes.filter(type =>
        type.name?.toLowerCase().includes(query) ||
        type.code?.toLowerCase().includes(query)
      )
      : this.businessTypes;
    this.filteredBusinessTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.businessTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * @method onSearchChanged
   * @description Debounces search input to prevent excessive filtering.
   * Waits 300ms after last keystroke.
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * @method onTypeChange
   * @description Checks for duplicate business type names during data entry.
   *
   * **Logic:**
   * - **Create Mode**: Checks if name exists in `businessTypes`.
   * - **Update Mode**: Checks if name exists in other records.
   *
   * @param {string} type - The business type name entered by the user.
   */
  public onTypeChange(type) {
    if (this.submitButton == true) {
      for (let i = 0; i < this.businessTypes.length; i++) {
        let obj = this.businessTypes[i];
        let histCulturecategory = obj.name?.toUpperCase() ?? '';
        if (histCulturecategory == type.toUpperCase()) {
          this.toasterService.info('This is already existed please enter other  business type');
          this.businessType.name = '';
          break;
        } else if (histCulturecategory.toUpperCase().indexOf(type.toUpperCase()) != -1 && type != '') {
          let booleanval = confirm('Similar name is there do you want to enter');
          if (!booleanval) {
            this.businessType.name = '';
          }
          break;
        }
      }
    } else if (this.updateButton == true) {
      this.originalBusinessTypes = _.filter(this.businessTypes, { 'id': (this.businessType.id) })[0].name;
      for (let i = 0; i < this.businessTypes.length; i++) {
        let obj = this.businessTypes[i];
        let histCulturecategory = obj.name.toUpperCase();
        if (histCulturecategory == type.toUpperCase() && obj.id != this.businessType.id) {
          this.toasterService.info('Business type already existed please enter other business type');
          this.businessType.name = '';
          this.businessType.name = _.filter(this.businessTypes, { 'id': (this.businessType.id) })[0].name;
          break;
        } else if (type.toUpperCase().indexOf(histCulturecategory) != -1 && type.toUpperCase() != this.originalBusinessTypes.toUpperCase() && type != '') {
          var booleanval = confirm('similar type is there do you want to enter');
          if (!booleanval) {
            this.businessType.name = _.filter(this.businessTypes, { 'id': (this.businessType.id) })[0].name;
          }
          break;
        }
      }
    }
  }

  /*Add soilType*/
  /**
   * @method addUpdateBusinessType
   * @async
   * @description Handles form submission for creating or updating business types.
   * 1. Validates form.
   * 2. Transforms `businesscategory` form control to `businessCategoryID` for payload.
   * 3. Calls appropriate service method (`create` or `updateOneByID`).
   *
   * @returns {Promise<void>}
   * @throws {Error} Displays error via `CommonMethodsService`.
   */
  public async addUpdateBusinessType() {
    this.submitted = true;

    if (this.addBusinessCategoryForm.invalid) {
      return;
    }
    // Deep clone form values to avoid mutation issues
    const rawData = this.addBusinessCategoryForm.getRawValue();
    let formData: any = {
      ...rawData,
      businessCategoryID: rawData.businesscategory
    };
    delete formData.businesscategory;
    try {
      this.businessTypeDoubleClick = true;
      if (this.submitButton) {
        await this.businessTypeService.create(formData);
        this.toasterService.success('Business type created successfully', '', { timeOut: 3000 });
      } else {
        const updateBusinessType = { ...formData };
        delete updateBusinessType.id;
        await this.businessTypeService.updateOneByID(this.businessType.id, updateBusinessType);
        this.toasterService.success('Business type updated successfully', '', { timeOut: 3000 });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.businessTypeDoubleClick = false;
    }
  }


  /*Get soil type by id*/
  /**
   * @method getBusinessTypeById
   * @description Prepares the UI for editing a specific business type.
   * - Finds type by ID.
   * - Populates form with existing data.
   * @param {string} id - The ID of the business type to edit.
   */
  public getBusinessTypeById(id: string) {
    this.businessTypeDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundBusinessType = this.businessTypesList.find(uom => uom?.id === id);
    this.businessType = foundBusinessType ? { ...foundBusinessType } : new BusinessTypeModel(); // clone object
    if (this.businessType) {
      this.addBusinessCategoryForm.patchValue({
        name: this.businessType.name,
        businesscategory: this.businessType.businessCategoryID,
        code: this.businessType.code,
        description: this.businessType.description
      });
    }
  }

  /**
   * @method clear
   * @description Resets the form and component state to default values.
   * Clears validation errors and hides the form.
   */
  public clear() {
    this.addBusinessCategoryForm.reset();
    this.businessType = new BusinessTypeModel();
    this.businessTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.showForm = false;
  }

  /**
   * @method openForm
   * @description Opens the form in "Create" mode.
   * Sets `submitButton` to true and recalculates layout.
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.addBusinessCategoryForm.controls).length;
  }
}
